import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2 } from "lucide-react";

export default function AuthPage() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") await login(username, password);
      else await register(username, password);
    } catch (err) {
      setError(err?.message || "Something went wrong. Please try again.");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-primary p-12 text-primary-foreground">
        <div className="relative z-10 flex items-center gap-2.5">
          <div className="grid h-10 w-10 place-items-center rounded-lg bg-white overflow-hidden">
            <img src="/logo.png" alt="AgriPulse logo" className="h-8 w-8 object-contain" />
          </div>
          <span className="font-display text-2xl font-extrabold tracking-tight">AgriPulse</span>
        </div>
        <div className="relative z-10">
          <h1 className="font-display text-4xl font-extrabold leading-tight tracking-tight">
            Every animal, activity<br />and coin — accounted for.
          </h1>
          <p className="mt-4 max-w-md text-primary-foreground/80">
            Your private livestock ledger. Track income, expenses, produce and get smart alerts when something looks off.
          </p>
        </div>
        <div className="relative z-10 text-sm text-primary-foreground/60 font-mono-nums">Livestock Finance & Operations</div>
        <div className="absolute -right-24 -bottom-24 h-96 w-96 rounded-full bg-accent/30 blur-3xl" />
        <div className="absolute right-10 top-10 h-40 w-40 rounded-full bg-primary-foreground/5 blur-2xl" />
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <Card className="w-full max-w-md p-8">
          <div className="flex items-center gap-2.5 lg:hidden mb-6">
            <div className="grid h-9 w-9 place-items-center rounded-lg bg-white border border-border overflow-hidden">
              <img src="/logo.png" alt="AgriPulse logo" className="h-7 w-7 object-contain" />
            </div>
            <span className="font-display text-xl font-extrabold tracking-tight">AgriPulse</span>
          </div>

          <h2 className="font-display text-2xl font-bold tracking-tight" data-testid="auth-title">
            {mode === "login" ? "Welcome back" : "Create your farm account"}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {mode === "login" ? "Sign in to your private farm ledger." : "Sign up with a username and password."}
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="username">Username</Label>
              <Input id="username" className="mt-1.5" value={username} autoComplete="username"
                onChange={(e) => setUsername(e.target.value)} placeholder="e.g. greenvalley"
                data-testid="auth-username-input" required />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" className="mt-1.5" value={password}
                autoComplete={mode === "login" ? "current-password" : "new-password"}
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                data-testid="auth-password-input" required />
            </div>

            {error && (
              <p className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-lg px-3 py-2" data-testid="auth-error">
                {error}
              </p>
            )}

            <Button type="submit" className="w-full rounded-full h-11" disabled={busy} data-testid="auth-submit-button">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {mode === "login" ? "Sign In" : "Create Account"}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            {mode === "login" ? "New to AgriPulse?" : "Already have an account?"}{" "}
            <button type="button" className="font-semibold text-accent hover:underline"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
              data-testid="auth-toggle-button">
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>
        </Card>
      </div>
    </div>
  );
}
